import type { DesignInfo } from "../replay/format";
import type { WorldState } from "../state/world";

/*
 * Colour rules: the base hue is the team, always. A categorical or binary-ish
 * state (unit type, out of ammo) tints the team colour toward that state's
 * hue, so teams stay readable; an analog measure (health, ammo) is shown as
 * saturation, fading toward grey as it drops.
 */

/** A categorical or binary state, shown as a tint. */
export type Mark = "none" | "type" | "dry" | "suppressed" | "wounded" | "dug";
/** An analog measure shown as saturation. */
export type Sat = "none" | "health" | "ammo" | "suppression" | "dug";

export type RGB = [number, number, number];

/** How far a tinted unit's team colour moves toward the tint hue. */
export const TINT = 0.6;

/** Tint hues for binary states. */
export const MARK_COLOURS: Record<Exclude<Mark, "none" | "type">, RGB> = {
  dry: [0.92, 0.93, 0.96],
  suppressed: [1, 0.2, 0.55],
  wounded: [1, 0.15, 0.1],
  dug: [0.55, 0.38, 0.2],
};

/** Unit kinds, for type tints (by sprite shape). */
export type Kind = "infantry" | "tank" | "truck" | "mortar" | "anti-tank" | "prisoners";

/** Type tints. Infantry, the mass of every army, keeps the plain team colour
 *  so the other kinds stand out. */
export const TYPE_TINTS: [Kind, string, RGB | null][] = [
  ["infantry", "Infantry", null],
  ["tank", "Tank", [1, 0.12, 0.12]],
  ["truck", "Truck", [0.2, 0.95, 0.45]],
  ["mortar", "Mortar", [0.85, 0.3, 1]],
  ["anti-tank", "Anti-tank", [1, 0.6, 0.1]],
  ["prisoners", "Prisoners", [1, 1, 1]],
];

/** The hue a unit is tinted toward, or null for none. */
export function tintOf(s: WorldState, d: DesignInfo, id: number, m: Mark, kind: Kind): RGB | null {
  switch (m) {
    case "none":
      return null;
    case "type":
      return TYPE_TINTS.find(([k]) => k === kind)![2];
    case "dry":
      return d.ammo > 0 && s.ammo[id] === 0 ? MARK_COLOURS.dry : null;
    case "suppressed":
      return s.suppression[id] >= 5 ? MARK_COLOURS.suppressed : null;
    case "wounded":
      return s.hp[id] * 100 < d.hp * 35 ? MARK_COLOURS.wounded : null;
    case "dug":
      return s.dug[id] >= 12 ? MARK_COLOURS.dug : null;
  }
}

/** Each ore's hue, as the world atlas draws it (the atlas renderer's
 *  palette): the atlas legend and the ore heatmap both use these, so iron
 *  is the same rust everywhere. Keyed by the sim's resource name. */
export const ORE_HUES: [string, string][] = [
  ["Iron", "#d0603a"],
  ["Copper", "#3cc8a8"],
  ["Coal", "#e8d27a"],
  ["Oil", "#c86ad8"],
  ["Bauxite", "#e8a060"],
  ["Magnesium", "#bfe6f2"],
  ["Lithium", "#6cc8ff"],
  ["RareEarths", "#ff80c0"],
  ["Nickel", "#8fa8c4"],
  ["Chromium", "#5fb060"],
  ["Manganese", "#a070b0"],
  ["Tungsten", "#6070e8"],
  ["Tin", "#c8c090"],
  ["Platinum", "#c8c8ff"],
  ["Sulfur", "#d8f048"],
  ["Uranium", "#40f078"],
];

/** Ores by family (for the heatmap's picker and its key's order). */
export const ORE_FAMILIES: [string, string[]][] = [
  ["Steel chain", ["Iron", "Coal"]],
  ["Copper and tin", ["Copper", "Tin"]],
  ["Fuels", ["Oil", "Sulfur", "Uranium"]],
  ["Light metals", ["Bauxite", "Magnesium", "Lithium"]],
  ["Alloying metals", ["Nickel", "Chromium", "Manganese", "Tungsten"]],
  ["Rare", ["RareEarths", "Platinum"]],
];

/** Each ore's name for players, by the sim's resource name: the server's
 *  (its state's `ores`), short of " ore"; until then, these. */
const oreNames = new Map<string, string>([
  ["RareEarths", "Rare earths"],
  ["Magnesium", "Magnesite"],
  ["Chromium", "Chromite"],
]);
export function setOreNames(list: { name: string; word: string }[]): void {
  for (const o of list) {
    const key = o.word
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("");
    oreNames.set(key, o.name.replace(/ ore$/, ""));
  }
}
/** "Rare earths" for "RareEarths"; a sim name it doesn't know, as it is. */
export const oreLabel = (ore: string): string => oreNames.get(ore) ?? ore;
/** The server's words ("rare_earths") and the sim's names ("RareEarths"). */
export const oreKey = (word: string): string =>
  word
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("");
export const oreWord = (key: string): string => key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
/** The ores a heatmap choice shows: "All", "family:<name>", or one ore. */
export function oresOf(choice: string): string[] {
  if (choice === "All") return ORE_HUES.map(([o]) => o);
  if (choice.startsWith("family:")) return ORE_FAMILIES.find(([f]) => f === choice.slice(7))?.[1] ?? [];
  return [choice];
}

export const oreRGB = (ore: string): RGB => {
  const hex = ORE_HUES.find(([o]) => o === ore)?.[1] ?? "#cccccc";
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

/** The haze over ore of a kind not known yet (an EM survey only). */
export const ORE_UNKNOWN: RGB = [0.78, 0.78, 0.76];

export function mixRGB(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** A unit's value for an analog measure, 0..1 (1 = good: full health, full
 *  ammo, not suppressed, dug in). Unarmed units count as full on ammo. */
export function metric(s: WorldState, d: DesignInfo, id: number, m: Exclude<Sat, "none">): number {
  switch (m) {
    case "health":
      return s.hp[id] / Math.max(1, d.hp);
    case "ammo":
      return d.ammo > 0 ? s.ammo[id] / d.ammo : 1;
    case "suppression":
      return 1 - s.suppression[id] / 15;
    case "dug":
      return s.dug[id] / 15;
  }
}

/** Fades `c` toward its own grey: `amount` 1 keeps it, 0 is nearly grey
 *  (kept a little coloured so teams stay readable). Squared, so half-empty
 *  already reads as clearly faded while full stays vivid. */
export function saturate(c: RGB, amount: number): RGB {
  const lum = 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];
  const v = Math.max(0, Math.min(1, amount));
  const k = 0.12 + 0.88 * v * v;
  return [lum + (c[0] - lum) * k, lum + (c[1] - lum) * k, lum + (c[2] - lum) * k];
}

export const MARK_LABELS: [Mark, string][] = [
  ["none", "Team"],
  ["type", "Type"],
  ["dry", "Out of ammo"],
  ["suppressed", "Suppressed"],
  ["wounded", "Wounded"],
  ["dug", "Dug in"],
];

export const SAT_LABELS: [Sat, string][] = [
  ["none", "Full"],
  ["health", "Health"],
  ["ammo", "Ammo"],
  ["suppression", "Suppression"],
  ["dug", "Dug in"],
];

/** What fading means, per measure. */
export const SAT_LOW: Record<Exclude<Sat, "none">, string> = {
  health: "wounded",
  ammo: "low on ammo",
  suppression: "pinned down",
  dug: "in the open",
};
